// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

console.log('🔧 API Base URL:', API_BASE_URL);

// En-têtes d'un appel JSON à l'API.
//
// ⛔ PLUS DE JETON ICI depuis le 30/09/2026 (chantier ⑯) : la session voyage
// dans un cookie `HttpOnly` que la page ne peut pas lire, et `lib/session.js`
// ajoute à chaque appel ce qu'il faut (cookie, jeton anti-CSRF, renouvellement).
// Le nom est resté pour ne pas réécrire ses quelque 90 appelants.
const getAuthHeaders = () => ({ 'Content-Type': 'application/json' });

// Acceptation CGU (premier login)
export const acceptCgu = async () => {
  const r = await fetch(`${API_BASE_URL}/api/auth/accept-cgu`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!r.ok) throw new Error('Erreur acceptation CGU');
  return r.json();
};

// Préférences du compte (Paramètres → Préférences, révisions 0022 et 0023) :
// `emails_consommation`, `email_fin_analyse`, `format_sortie_defaut`.
export const getPreferences = async () => {
  const r = await fetch(`${API_BASE_URL}/api/auth/preferences`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error('Erreur préférences');
  return r.json();
};

// `modifications` : un sous-ensemble, ex. `{ email_fin_analyse: true }`.
export const setPreferences = async (modifications) => {
  const r = await fetch(`${API_BASE_URL}/api/auth/preferences`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(modifications),
  });
  if (!r.ok) throw new Error('Erreur préférences');
  return r.json();
};

// Suivi des analyses (chantier ⑮) : celles qui tournent encore et celles dont
// le résultat n'a pas été vu — ce que dit le bandeau à la réouverture.
export const getSuiviAnalyses = async () => {
  const r = await fetch(`${API_BASE_URL}/api/suivi-analyses`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error('Erreur suivi des analyses');
  return r.json(); // { en_cours: [{id, filename, status, progress}], terminees: [{id, filename, completion_date}] }
};

// Annule une analyse en cours (ou en file). Le serveur la passe en CANCELLED,
// arrête son traitement et prévient l'onglet qui la suit (SocketIO). Lève une
// erreur portant le message du serveur (analyse déjà terminée, pas la sienne…).
export const annulerAnalyse = async (analysisId) => {
  const r = await fetch(`${API_BASE_URL}/api/analyses/${analysisId}/cancel`, {
    method: 'POST', headers: getAuthHeaders(),
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.error || 'Erreur annulation');
  }
  return r.json();
};

// Une analyse « en cours » au sens de l'utilisateur : en traitement, ou en
// file (PENDING avec `queued_at`). Un fichier déposé mais jamais lancé n'en est
// pas une — il se supprime, il ne s'annule pas.
export const estEnCours = (analysis) =>
  analysis?.status === 'PROCESSING' || (analysis?.status === 'PENDING' && !!analysis?.queued_at);

// Le compte a vu la fin de cette analyse : le bandeau n'en parle plus. Sans
// effet si l'appelant n'en est pas l'auteur, ou si elle n'est pas terminée.
export const marquerResultatVu = (analysisId) =>
  fetch(`${API_BASE_URL}/api/analyses/${analysisId}/vue`, { method: 'POST', headers: getAuthHeaders() })
    .catch(() => {});

// Calendrier des versions des CGU/CGV (`core/cgv.py`). Route PUBLIQUE : la page
// /cgv l'appelle aussi pour un visiteur non connecté.
export const getCgvVersions = async () => {
  const r = await fetch(`${API_BASE_URL}/api/cgv/versions`);
  if (!r.ok) throw new Error('Erreur calendrier CGV');
  return r.json();
};

// Upload PDF
// `norme` (T2) : le code de la norme choisie. Absent, le serveur prend
// l'unique norme installée — comportement d'avant le registre.
export const uploadPDF = async (file, norme = null) => {
  const formData = new FormData();
  formData.append('file', file);
  if (norme) formData.append('norme', norme);

  try {
    // ❌ PAS de Content-Type : FormData le pose lui-même, avec sa frontière.
    const response = await fetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();
    await handleApiResponse(response);  // ✅ Intercepte 401
    if (!response.ok) {
      const error = new Error(data.error || 'Erreur upload');
      error.response = { data }; // ✅ Passer la réponse complète
      throw error;
    }

    return data;
  } catch (error) {
    console.error('❌ Erreur uploadPDF:', error);
    throw error;
  }
};

// ============================================
// startAnalysis — mise à jour (remplacer l'existant)
// ============================================
export const startAnalysis = async (analysisId, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/start-analysis/${analysisId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      const error = await response.json();
      // `message` d'abord : le serveur y met une phrase lisible quand il en a
      // une (extraction projet en cours, quota épuisé). `error` seul renvoyait
      // un code technique en anglais dans une popup.
      const err = new Error(error.message || error.error || 'Démarrage échoué');
      // Le code survit au throw : l'appelant doit pouvoir distinguer un refus
      // transitoire (relancer plus tard) d'un échec définitif.
      err.code = error.error_code || error.error;
      err.status = response.status;
      throw err;
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Start analysis error:', error);
    throw error;
  }
};

// Demande d’assistance depuis /aide (étape 2, 31/08/2026).
//
// ⚠️ Ne PAS confondre avec `/api/contact` : celui-là est le formulaire
// PROSPECT des sites vitrines (SIRET, société et adresse postale obligatoires,
// ouvert sans authentification). Celui-ci est un canal d’assistance pour un
// compte existant — trois champs, dont un facultatif.
//
// La réponse 502 est SIGNIFICATIVE : elle porte `destinataire`, l’adresse à
// laquelle l’utilisateur doit reprendre son message à la main. ⛔ Ne pas
// l’avaler dans un « une erreur est survenue » générique.
export const envoyerDemandeAide = async ({ situation, description, analysisId }) => {
  const response = await fetch(`${API_BASE_URL}/api/aide`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      situation,
      description,
      analysis_id: analysisId || '',
    }),
  });
  // ⛔ PAS de `handleApiResponse` ici — voir `chargerAnalysesPourAide`.
  // /aide est publique : un 401 doit se dire dans le formulaire, avec l’adresse
  // de repli, et non éjecter la personne vers /login au moment où elle demande
  // de l’aide.
  const data = await response.json().catch(() => ({}));
  if (response.status === 401) {
    const erreur = new Error('Votre session a expiré. Reconnectez-vous, ou écrivez-nous directement.');
    erreur.code = 'session_expiree';
    erreur.destinataire = 'support@normascan.fr';
    throw erreur;
  }
  if (!response.ok) {
    const erreur = new Error(data.message || 'Envoi impossible');
    erreur.code = data.error;
    erreur.destinataire = data.destinataire;
    erreur.champs = data.fields;
    throw erreur;
  }
  return data;
};
// Liste des analyses POUR LA PAGE D’AIDE — lecture inerte.
//
// 💣 ⛔ NE PAS remplacer par `getAnalyses` : celui-ci passe par
// `handleApiResponse`, qui sur un 401 efface le `localStorage` et fait
// `window.location.href = '/login?expired=true'`.
//
// Or /aide est une page PUBLIQUE, et ce chargement est PASSIF : il part au
// montage, sans que personne n’ait cliqué. Un visiteur arrivant avec un jeton
// périmé — onglet de la veille, session expirée — était donc éjecté vers
// l’écran de connexion au moment précis où il venait y lire « je n’arrive plus
// à me connecter ». Constaté à l’écran le 31/08/2026.
//
// Ici, un 401 n’est pas une erreur : c’est la réponse « pas de session
// utilisable », et elle sert à masquer le formulaire.
export const chargerAnalysesPourAide = async () => {
  try {
    const r = await fetch(`${API_BASE_URL}/api/analyses`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    if (!r.ok) return { session: false, analyses: [] };
    const d = await r.json();
    return { session: true, analyses: d?.analyses || [] };
  } catch (e) {
    return { session: false, analyses: [] };
  }
};

// Liste analyses
export const getAnalyses = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/analyses`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);  // ✅ Intercepte 401
    if (!response.ok) {
      throw new Error('Fetch échoué');
    }
    
    return await response.json();
  } catch (error) {
    console.error('❌ Get analyses error:', error);
    throw error;
  }
};

// Détail analyse
export const getAnalysis = async (analysisId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/analyses/${analysisId}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    await handleApiResponse(response);  // ✅ Intercepte 401
    if (!response.ok) {
      throw new Error('Fetch échoué');
    }
    
    return await response.json();
  } catch (error) {
    console.error('❌ Get analysis error:', error);
    throw error;
  }
};

// Rapport PDF et export Excel d'une analyse — cf `telechargerFichier` (jamais
// de jeton dans l'URL). Le serveur donne le vrai nom ; celui-ci n'est qu'un repli.
export const telechargerRapport = (analysisId) =>
  telechargerFichier(`${API_BASE_URL}/api/analyses/${analysisId}/report`,
                     `rapport_${analysisId}.pdf`);

export const telechargerExcel = (analysisId) =>
  telechargerFichier(`${API_BASE_URL}/api/analyses/${analysisId}/excel`,
                     `analyse_${analysisId}.xlsx`);

// ============================================
// ANNOTATIONS
// ============================================

export const getAnnotations = async (analysisId) => {
  const response = await fetch(`${API_BASE_URL}/api/analyses/${analysisId}/annotations`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const createAnnotation = async (analysisId, payload) => {
  const response = await fetch(`${API_BASE_URL}/api/analyses/${analysisId}/annotations`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const updateAnnotation = async (annotationId, content) => {
  const response = await fetch(`${API_BASE_URL}/api/annotations/${annotationId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ content }),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const deleteAnnotation = async (annotationId) => {
  const response = await fetch(`${API_BASE_URL}/api/annotations/${annotationId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const getAdminAnnotations = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.types?.length)        params.set('types',        filters.types.join(','));
  if (filters.analysis_ids?.length) params.set('analysis_ids', filters.analysis_ids.join(','));
  if (filters.user_ids?.length)     params.set('user_ids',     filters.user_ids.join(','));
  if (filters.date_from)            params.set('date_from',    filters.date_from);
  if (filters.date_to)              params.set('date_to',      filters.date_to);
  if (filters.ann_statuses?.length) params.set('ann_statuses', filters.ann_statuses.join(','));
  
  const response = await fetch(`${API_BASE_URL}/api/admin/annotations?${params}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

// ============================================
// RETOURS D'AVIS — « cet avis est faux » (S3, 25/08/2026)
// ============================================
//
// ⚠️ Ces appels n'ont PAS le même droit d'accès que les annotations
// ci-dessus. Les annotations sont réservées aux rôles Evalens et aux sièges
// portant `lic_annotations_enabled` ; les retours sont ouverts à tout compte
// authentifié, sur ses propres analyses. C'est délibéré : mesuré le
// 25/08/2026, le canal verrouillé des annotations avait produit ZÉRO ligne, et
// la boucle de retour n'a d'intérêt que si chaque usage client en produit.

/** Les positions NON favorables d'une analyse, et les retours déjà déposés. */
export const getAnalysisPositions = async (analysisId) => {
  const response = await fetch(`${API_BASE_URL}/api/analyses/${analysisId}/positions`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

/**
 * Dépose un retour.
 * `kind: 'position'`     → { rule_number, norme, position_attendue, commentaire }
 * `kind: 'ecart_manque'` → { commentaire } seul (aucune règle ne l'a produit).
 */
export const createRuleFeedback = async (analysisId, payload) => {
  const response = await fetch(`${API_BASE_URL}/api/analyses/${analysisId}/retours`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const deleteRuleFeedback = async (retourId) => {
  const response = await fetch(`${API_BASE_URL}/api/retours/${retourId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const getAdminRuleFeedback = async ({ traitement, kind, limit, offset } = {}) => {
  const params = new URLSearchParams();
  if (traitement) params.set('traitement', traitement);
  if (kind)       params.set('kind', kind);
  if (limit != null)  params.set('limit', limit);
  if (offset != null) params.set('offset', offset);
  const response = await fetch(`${API_BASE_URL}/api/admin/retours?${params}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const adminUpdateRuleFeedback = async (retourId, payload) => {
  const response = await fetch(`${API_BASE_URL}/api/admin/retours/${retourId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

/** Télécharge une feuille au format S0, relisible telle quelle par `noter_s0.py`. */
export const exportRuleFeedbackS0 = (feuille) =>
  telechargerFichier(`${API_BASE_URL}/api/admin/retours/export?feuille=${feuille}`,
                     `s0_${feuille}.csv`);

// ============================================
// SUGGESTIONS
// ============================================

export const getSuggestions = async () => {
  const response = await fetch(`${API_BASE_URL}/api/suggestions`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const createSuggestion = async (payload) => {
  const response = await fetch(`${API_BASE_URL}/api/suggestions`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const updateSuggestion = async (suggestionId, payload) => {
  const response = await fetch(`${API_BASE_URL}/api/suggestions/${suggestionId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

// ============================================
// RATINGS
// ============================================

export const createRating = async (analysisId, payload) => {
  const response = await fetch(`${API_BASE_URL}/api/analyses/${analysisId}/rating`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const getRating = async (analysisId) => {
  const response = await fetch(`${API_BASE_URL}/api/analyses/${analysisId}/rating`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const getAdminRatings = async () => {
  const response = await fetch(`${API_BASE_URL}/api/admin/ratings`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};
// ============================================
// ACCOUNT
// ============================================

export const changePassword = async (currentPassword, newPassword) => {
  const response = await fetch(`${API_BASE_URL}/api/auth/password`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

// Nom de fichier annoncé par le serveur (`Content-Disposition`), ou null.
// `filename*` (RFC 5987, encodé UTF-8) passe avant `filename` : Flask ne met
// dans ce dernier qu'une version ASCII du nom quand il porte des accents.
const nomDepuisEntete = (entete) => {
  if (!entete) return null;
  const etendu = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(entete);
  if (etendu) {
    try { return decodeURIComponent(etendu[1].trim()); } catch (_) { /* repli */ }
  }
  const simple = /filename\s*=\s*"?([^";]+)"?/i.exec(entete);
  return simple ? simple[1].trim() : null;
};

// Télécharge un fichier protégé et le remet à l'utilisateur.
//
// ⛔ JAMAIS de jeton dans l'URL (`?token=`) : il se retrouve dans les journaux
// du serveur, l'historique du navigateur et l'en-tête `Referer`. Le serveur
// n'accepte plus le paramètre d'URL depuis le 21/09/2026 (`require_auth_download`).
// La session voyage en cookie (chantier ⑯) ; le `fetch` + blob reste, parce
// qu'il permet de contrôler l'erreur avant de remettre un fichier (cf plus bas).
//
// ⚠️ Le contrôle de `r.ok` AVANT `blob()` n'est pas cosmétique : une réponse
// d'erreur est du JSON, et l'enrober dans un blob produirait un fichier
// illisible que l'utilisateur croirait être son export. L'erreur remonte à
// l'appelant, qui l'affiche.
//
// Le nom vient du serveur quand il le donne (`nomParDefaut` sinon) : le CORS
// expose `Content-Disposition` pour que le développement, sur une autre
// origine, le lise aussi.
//
// `revokeObjectURL` libère le blob : sans lui, le fichier reste en mémoire de
// l'onglet jusqu'au rechargement de la page, ce qui compte quand une archive
// pèse plusieurs centaines de Mo.
export const telechargerFichier = async (url, nomParDefaut) => {
  const r = await fetch(url);
  if (!r.ok) {
    let message = 'Erreur de téléchargement';
    try {
      const d = await r.json();
      message = d.message || d.error || message;
    } catch (_) { /* réponse non-JSON : on garde le message générique */ }
    throw new Error(message);
  }
  const blob = await r.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = nomDepuisEntete(r.headers.get('Content-Disposition')) || nomParDefaut;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 0);
};

export const exportMyData = () =>
  telechargerFichier(`${API_BASE_URL}/api/auth/export-data`,
                     `normascan_export_${Date.now()}.zip`);

// Export RGPD d'un compte tiers par un administrateur (Art. 20). Sert à honorer
// une demande arrivée à privacy@evalens.fr, que rien ne permettait de traiter
// avant le 5/08/2026 sans requête SQL directe sur le serveur.
export const adminExportUserData = (userId, email) =>
  telechargerFichier(`${API_BASE_URL}/api/admin/users/${userId}/export-data`,
                     `normascan_export_${email || userId}_${Date.now()}.zip`);

// Journal d'audit — Evalens uniquement. `filtres` est un objet plat dont les
// entrées vides sont écartées : envoyer `?email=` filtrerait sur la chaîne vide
// au lieu de ne pas filtrer.
export const getAuditLogs = async (filtres = {}) => {
  const params = new URLSearchParams();
  Object.entries(filtres).forEach(([cle, valeur]) => {
    if (valeur !== '' && valeur !== null && valeur !== undefined) params.append(cle, valeur);
  });
  const r = await fetch(`${API_BASE_URL}/api/admin/audit-logs?${params}`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const getAuditLogActions = async () => {
  const r = await fetch(`${API_BASE_URL}/api/admin/audit-logs/actions`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Ce que la suppression détruirait, en chiffres réels. Alimente la 2e étape de
// l'écran de confirmation : « 47 analyses, 3 projets » arrête quelqu'un que
// « êtes-vous sûr ? » n'arrête plus au troisième écran.
export const getDeletionPreview = async () => {
  const r = await fetch(`${API_BASE_URL}/api/auth/deletion-preview`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Effacement d'un siège de licence — Evalens uniquement, sur demande écrite de
// l'entreprise. Un license_admin n'a pas accès à cette route (403) : il ne
// dispose que de la révocation, qui coupe l'accès sans rien détruire.
export const adminDeleteLicenseSeatData = async (licenseId, userId) => {
  const r = await fetch(
    `${API_BASE_URL}/api/admin/enterprise-licenses/${licenseId}/users/${userId}/data`,
    {
      method: 'DELETE',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirmation: 'SUPPRIMER LES DONNEES' }),
    });
  const data = await r.json();
  if (!r.ok) throw new Error(data.message || data.error || 'Erreur suppression');
  return data;
};

export const deleteAccount = async (confirmation) => {
  const r = await fetch(`${API_BASE_URL}/api/auth/delete-account`, {
    method: 'DELETE',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirmation })
  });
  const data = await r.json();
  // `message` AVANT `error` : quand les deux sont présents, `error` porte un code
  // technique (`license_seat`, `confirmation_required`) et `message` la phrase
  // destinée à l'utilisateur. L'ordre inverse affichait le code brut à l'écran.
  if (!r.ok) throw new Error(data.message || data.error || 'Erreur suppression');
  return data;
};

export const getUserStats = async () => {
  const response = await fetch(`${API_BASE_URL}/api/auth/stats`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

// `granularite` vaut 'mois' ou 'annee' ; `annee`/`mois` désignent la période
// consultée. Non transmis = le serveur retient le mois courant.
const ajouterPeriode = (params, { granularite, annee, mois } = {}) => {
  if (granularite) params.set('granularite', granularite);
  if (annee) params.set('annee', String(annee));
  if (mois) params.set('mois', String(mois));
  return params;
};

export const getMyFullStats = async ({ typeRapport, granularite, annee, mois } = {}) => {
  const params = new URLSearchParams();
  if (typeRapport) params.set('type_rapport', typeRapport);
  ajouterPeriode(params, { granularite, annee, mois });
  const response = await fetch(`${API_BASE_URL}/api/stats/me?${params}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

// Membre et échelon peuvent être envoyés ENSEMBLE : le serveur applique la
// précédence membre > échelon (`if/elif`). L'interface garde l'échelon
// sélectionné comme contexte pendant qu'on choisit un membre à l'intérieur.
export const getLicenseStats = async ({ userId, typeRapport, orgUnitId, societe, granularite, annee, mois } = {}) => {
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  if (orgUnitId) params.set('org_unit_id', orgUnitId);
  if (typeRapport) params.set('type_rapport', typeRapport);
  if (societe) params.set('societe', societe);
  ajouterPeriode(params, { granularite, annee, mois });
  const response = await fetch(`${API_BASE_URL}/api/license/stats?${params}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const exportLicenseConsommations = ({ userId, orgUnitId, granularite, annee, mois } = {}) => {
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  if (orgUnitId) params.set('org_unit_id', orgUnitId);
  ajouterPeriode(params, { granularite, annee, mois });
  return telechargerFichier(`${API_BASE_URL}/api/license/consommations/export?${params}`,
                            'consommations.xlsx');
};

// Annotations et évaluations sont plafonnées à 200 lignes CÔTÉ SERVEUR, et le
// filtre y est appliqué avant ce plafond : sans passer membre/échelon à l'API,
// filtrer dans le navigateur ferait passer une agence pour vide dès que la
// licence dépasse 200 lignes au total.
const chargerContenuLicence = async (chemin, cle, { userId, orgUnitId } = {}) => {
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  if (orgUnitId) params.set('org_unit_id', orgUnitId);
  const response = await fetch(`${API_BASE_URL}${chemin}?${params}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return (await response.json())[cle] || [];
};

export const getLicenseAnnotations = (filtres) =>
  chargerContenuLicence('/api/license/annotations', 'annotations', filtres);

export const getLicenseRatings = (filtres) =>
  chargerContenuLicence('/api/license/ratings', 'ratings', filtres);

// ⛔ Pas de `userId` : les résultats par règle ne se filtrent jamais par membre
// (15/09/2026) — le serveur l'ignorerait de toute façon.
export const exportLicenseResultatsAnalyses = ({ typeRapport, orgUnitId, societe } = {}) => {
  const params = new URLSearchParams();
  if (orgUnitId) params.set('org_unit_id', orgUnitId);
  if (societe) params.set('societe', societe);
  if (typeRapport) params.set('type_rapport', typeRapport);
  return telechargerFichier(`${API_BASE_URL}/api/license/resultats-analyses/export?${params}`,
                            'resultats_analyses.xlsx');
};

// ── Échelons d'organisation ──────────────────────────────────────────────
// `pool_used` et `nb_membres` sont calculés par le serveur ; la liste arrive
// déjà triée du plus englobant au plus fin, l'arbre s'affiche sans le
// reconstruire.
//
// `licenseId` n'est utile qu'à un admin/dev Evalens, qui n'a pas de licence à
// lui et doit donc désigner celle qu'il monte (livraison d'un contrat, avant
// que le client ait un administrateur). Un license_admin l'omet : le serveur
// ignore ce paramètre pour lui et retient toujours SA licence.
const suffixeLicence = (licenseId) => (licenseId ? `?license_id=${encodeURIComponent(licenseId)}` : '');

export const getLicenseOrgUnits = async (licenseId) => {
  const response = await fetch(`${API_BASE_URL}/api/license/org-units${suffixeLicence(licenseId)}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const createLicenseOrgUnit = async (payload, licenseId) => {
  const response = await fetch(`${API_BASE_URL}/api/license/org-units`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(licenseId ? { ...payload, license_id: licenseId } : payload),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

export const updateLicenseOrgUnit = async (orgUnitId, payload, licenseId) => {
  const response = await fetch(`${API_BASE_URL}/api/license/org-units/${orgUnitId}`, {
    method: 'PATCH',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(licenseId ? { ...payload, license_id: licenseId } : payload),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

// Le serveur refuse (409) tant que l'échelon porte des membres ou des
// sous-échelons, et rend un message déjà rédigé pour l'utilisateur final :
// on le remonte tel quel plutôt que d'en réécrire un moins précis.
export const deleteLicenseOrgUnit = async (orgUnitId, licenseId) => {
  const response = await fetch(
    `${API_BASE_URL}/api/license/org-units/${orgUnitId}${suffixeLicence(licenseId)}`,
    { method: 'DELETE', headers: getAuthHeaders() }
  );
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

// ============================================
// MFA (activation/désactivation self-service — session normale déjà active)
// ============================================

// tempToken : jeton PROVISOIRE du login (étape du 2e facteur, pas encore de
// session). Il ne vaut que pour les 3 routes MFA, 15 min, une fois ; il reste
// en mémoire (état React), jamais sur le disque. Sans lui, la session normale
// (cookie) sert — activation depuis le profil.
const mfaAuthHeaders = (tempToken) => tempToken
  ? { 'Authorization': `Bearer ${tempToken}`, 'Content-Type': 'application/json' }
  : getAuthHeaders();

export const setupMfa = async (tempToken) => {
  const response = await fetch(`${API_BASE_URL}/api/auth/mfa/setup`, {
    method: 'POST',
    headers: mfaAuthHeaders(tempToken),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json(); // { secret, qr_code }
};

export const confirmMfaSetup = async (code, tempToken) => {
  const response = await fetch(`${API_BASE_URL}/api/auth/mfa/setup/confirm`, {
    method: 'POST',
    headers: mfaAuthHeaders(tempToken),
    body: JSON.stringify({ code }),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json(); // { success, recovery_codes } OU { user, recovery_codes } + cookies si setup forcé
};

export const verifyMfaLogin = async (tempToken, code) => {
  const response = await fetch(`${API_BASE_URL}/api/auth/mfa/verify`, {
    method: 'POST',
    headers: mfaAuthHeaders(tempToken),
    body: JSON.stringify({ code }),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json(); // { user } + cookies de session
};

export const disableMfa = async (password) => {
  const response = await fetch(`${API_BASE_URL}/api/auth/mfa`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    body: JSON.stringify({ password }),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

// ============================================
// ADMIN STATS
// ============================================

export const getAdminStats = async () => {
  const response = await fetch(`${API_BASE_URL}/api/admin/stats`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error((await response.json()).error || 'Erreur');
  return response.json();
};

// ✅ V3.2.1: Intercepteur global 401
// Un 401 arrivé JUSQU'ICI a déjà survécu à un renouvellement (lib/session.js) :
// la session est vraiment finie.
export const handleApiResponse = async (response) => {
  if (response.status === 401) {
    window.location.href = '/login?expired=true';
    throw new Error('Session expirée');
  }
  return response;
};
// Admin DELETE
export const adminDeleteAnnotation = async (id) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/annotations/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};
export const adminDeleteRating = async (id) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/ratings/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};
export const adminDeleteSuggestion = async (id) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/suggestions/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Admin EXPORT — cf `telechargerFichier` (jamais de jeton dans l'URL)
export const exportAnnotations  = () => telechargerFichier(`${API_BASE_URL}/api/admin/annotations/export`, 'annotations_export.xlsx');
export const exportRatings       = () => telechargerFichier(`${API_BASE_URL}/api/admin/ratings/export`, 'evaluations_export.xlsx');
export const exportSuggestions   = () => telechargerFichier(`${API_BASE_URL}/api/admin/suggestions/export`, 'suggestions_export.xlsx');
export const exportBillingSummary = (month) => telechargerFichier(`${API_BASE_URL}/api/admin/billing-summary/export?month=${month}`, `rentabilite_${month}.xlsx`);

// Admin GET suggestions
export const getAdminSuggestions = async () => {
  const r = await fetch(`${API_BASE_URL}/api/suggestions`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Update annotation statuts
export const updateAnnotationStatus = async (id, payload) => {
  const r = await fetch(`${API_BASE_URL}/api/annotations/${id}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};
// Admin counts (badges)
export const getAdminCounts = async () => {
  const r = await fetch(`${API_BASE_URL}/api/admin/counts`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Admin users
export const getAdminUsers = async () => {
  const r = await fetch(`${API_BASE_URL}/api/admin/users`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const adminUpdateUser = async (userId, payload) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
    method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const adminDeleteUser = async (userId) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
    method: 'DELETE', headers: getAuthHeaders(),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Fiche client (chantier SaaS 5bis) : identité, abonnement, factures, consommation.
export const getFicheClient = async (userId) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/fiche`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const updateIdentiteClient = async (userId, payload) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/identite`, {
    method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Admin billing (rentabilité par user/mois)
// `vue` : 'mois' (défaut), 'glissant' (12 mois qui s'achèvent avec `month`),
// ou 'arr' (le mois × 12 — une PROJECTION, que la réponse signale).
export const getBillingSummary = async (month, vue = 'mois') => {
  const r = await fetch(`${API_BASE_URL}/api/admin/billing-summary?month=${month}&vue=${vue}`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Admin tarifs Mistral (coût interne, éditable sans redéploiement)
export const getMistralPricing = async () => {
  const r = await fetch(`${API_BASE_URL}/api/admin/mistral-pricing`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const updateMistralPricing = async (payload) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/mistral-pricing`, {
    method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const refreshMistralRate = async () => {
  const r = await fetch(`${API_BASE_URL}/api/admin/mistral-pricing/refresh-rate`, {
    method: 'POST', headers: getAuthHeaders(),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Admin stats — Consommations (typologie d'appels LLM)
export const getConsommations = async ({ scope = 'total', userId, tierCode } = {}) => {
  const params = new URLSearchParams({ scope });
  if (userId) params.set('user_id', userId);
  if (tierCode) params.set('tier_code', tierCode);
  const r = await fetch(`${API_BASE_URL}/api/admin/consommations?${params}`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const exportConsommations = ({ scope = 'total', userId, tierCode } = {}) => {
  const params = new URLSearchParams({ scope });
  if (userId) params.set('user_id', userId);
  if (tierCode) params.set('tier_code', tierCode);
  return telechargerFichier(`${API_BASE_URL}/api/admin/consommations/export?${params}`,
                            'consommations.xlsx');
};

// Admin stats — Résultats des analyses (avis par règle métier)
export const getResultatsAnalyses = async (filters = {}) => {
  const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v)));
  const r = await fetch(`${API_BASE_URL}/api/admin/resultats-analyses?${params}`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const exportResultatsAnalyses = (filters = {}) => {
  const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v)));
  return telechargerFichier(`${API_BASE_URL}/api/admin/resultats-analyses/export?${params}`,
                            'resultats_analyses.xlsx');
};

// Pagination analyses
export const getAnalysesPaginated = async (offset = 0, limit = 20, search = '') => {
  const params = new URLSearchParams({ offset, limit });
  if (search) params.set('search', search);
  const r = await fetch(`${API_BASE_URL}/api/analyses?${params}`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// ============================================
// BILLING — Stripe
// ============================================

export const getBillingSubscription = async () => {
  const r = await fetch(`${API_BASE_URL}/api/billing/subscription`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const createCheckoutSession = async (priceId) => {
  const r = await fetch(`${API_BASE_URL}/api/billing/create-checkout-session`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ price_id: priceId }),
  });
  if (!r.ok) {
    // Le CODE voyage avec l'erreur : `identite_requise` (409) doit ouvrir la page
    // d'identité, pas afficher une erreur générique.
    const corps = await r.json().catch(() => ({}));
    const erreur = new Error(corps.error || 'Erreur');
    erreur.code = corps.code;
    throw erreur;
  }
  return r.json();
};

// Identité de facturation d'avant paiement (chantier SaaS 1, 23/09/2026).
export const getIdentiteFacturation = async () => {
  const r = await fetch(`${API_BASE_URL}/api/billing/identite`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const putIdentiteFacturation = async (payload) => {
  const r = await fetch(`${API_BASE_URL}/api/billing/identite`, {
    method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(payload),
  });
  const corps = await r.json().catch(() => ({}));
  if (!r.ok) {
    const erreur = new Error(corps.error || 'Erreur');
    erreur.code = corps.code;
    erreur.champsManquants = corps.champs_manquants || [];
    throw erreur;
  }
  return corps;
};

// Chantier ⑩ : certifications EN COURS d'un diagnostiqueur (annuaire officiel ADI).
// Le code voyage avec l'erreur : `limite_atteinte` (429) et `palier_insuffisant`
// (403) ont chacun leur message à l'écran.
export const rechercherCertification = async (prenom, nom) => {
  const params = new URLSearchParams({ prenom, nom });
  const r = await fetch(`${API_BASE_URL}/api/certifications/recherche?${params}`,
                        { headers: getAuthHeaders() });
  const corps = await r.json().catch(() => ({}));
  if (!r.ok) {
    const erreur = new Error(corps.error || 'Erreur');
    erreur.code = corps.code;
    throw erreur;
  }
  return corps;
};

export const consulterRegistreEntreprises = async (siren) => {
  const r = await fetch(`${API_BASE_URL}/api/billing/identite/registre?siren=${encodeURIComponent(siren)}`,
                        { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Au retour de Checkout : le serveur relit la session chez Stripe et applique
// l'abonnement SANS attendre le webhook, qui peut manquer ou arriver après le
// client (constaté le 22/09/2026 : rachat payé, Normascan toujours « résilié »).
export const confirmCheckoutSession = async (sessionId) => {
  const r = await fetch(`${API_BASE_URL}/api/billing/checkout/confirm`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ session_id: sessionId }),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Ce que coûtera un changement de forfait, AVANT validation. Le montant vient
// de Stripe : c'est le seul chiffre qu'on ait le droit d'annoncer, puisque
// c'est celui qui sera prélevé.
export const previewPlanChange = async (priceId) => {
  const r = await fetch(`${API_BASE_URL}/api/billing/preview-plan-change`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ price_id: priceId }),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Annule une bascule de palier programmée : le client conserve son forfait.
export const cancelPlanChange = async () => {
  const r = await fetch(`${API_BASE_URL}/api/billing/cancel-plan-change`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Abandonne une montée en gamme laissée impayée : la facture est annulée.
export const abandonPendingChange = async () => {
  const r = await fetch(`${API_BASE_URL}/api/billing/abandon-pending-change`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Lève une résiliation programmée : l'abonnement se poursuit. Rien n'est
// prélevé — l'abonnement n'a jamais cessé, on retire la borne posée sur sa fin.
export const resumeSubscription = async () => {
  const r = await fetch(`${API_BASE_URL}/api/billing/resume-subscription`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const createPortalSession = async () => {
  const r = await fetch(`${API_BASE_URL}/api/billing/create-portal-session`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Pagination admin annotations
export const getAdminAnnotationsPaginated = async (filters = {}, offset = 0, limit = 50) => {
  const params = new URLSearchParams({ offset, limit });
  if (filters.types?.length)        params.set('types',        filters.types.join(','));
  if (filters.analysis_ids?.length) params.set('analysis_ids', filters.analysis_ids.join(','));
  if (filters.user_ids?.length)     params.set('user_ids',     filters.user_ids.join(','));
  if (filters.date_from)            params.set('date_from',    filters.date_from);
  if (filters.date_to)              params.set('date_to',      filters.date_to);
  if (filters.ann_statuses?.length) params.set('ann_statuses', filters.ann_statuses.join(','));
  const r = await fetch(`${API_BASE_URL}/api/admin/annotations?${params}`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// Pagination admin ratings
export const getAdminRatingsPaginated = async (offset = 0, limit = 50) => {
  const params = new URLSearchParams({ offset, limit });
  const r = await fetch(`${API_BASE_URL}/api/admin/ratings?${params}`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};
// ============================================
// PROJETS + RAG
// ============================================

export const getProjects = async () => {
  const r = await fetch(`${API_BASE_URL}/api/projects`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const getProject = async (projectId) => {
  const r = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const createProject = async (payload) => {
  const r = await fetch(`${API_BASE_URL}/api/projects`, {
    method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const updateProject = async (projectId, payload) => {
  const r = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
    method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const deleteProject = async (projectId) => {
  const r = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
    method: 'DELETE', headers: getAuthHeaders(),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const addProjectDocumentFile = async (projectId, file, docType) => {
  const form = new FormData();
  form.append('file', file);
  form.append('doc_type', docType);
  const r = await fetch(`${API_BASE_URL}/api/projects/${projectId}/documents`, {
    method: 'POST',
    body: form,
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const addProjectDocumentManual = async (projectId, structuredData) => {
  const r = await fetch(`${API_BASE_URL}/api/projects/${projectId}/documents`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ doc_type: 'saisie_manuelle', structured_data: structuredData }),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const toggleProjectDocument = async (projectId, docId, isActive) => {
  const r = await fetch(`${API_BASE_URL}/api/projects/${projectId}/documents/${docId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ is_active: isActive }),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const validateProject = async (projectId) => {
  const r = await fetch(`${API_BASE_URL}/api/projects/${projectId}/validate`, {
    method: 'POST', headers: getAuthHeaders(),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const getProjectAnalyses = async (projectId) => {
  const r = await fetch(`${API_BASE_URL}/api/projects/${projectId}/analyses`, { headers: getAuthHeaders() });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

export const deleteProjectDocument = async (projectId, docId) => {
  const r = await fetch(`${API_BASE_URL}/api/projects/${projectId}/documents/${docId}`, {
    method: 'DELETE', headers: getAuthHeaders(),
  });
  if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
  return r.json();
};

// ── Facturation des licences entreprise (QONTO) ──────────────────────────────
//
// Deux gestes séparés, volontairement : `createLicenseInvoice` ne calcule et
// n'enregistre qu'en local, `finalizeLicenseInvoice` seul transmet à QONTO et
// consomme un numéro de la séquence légale. Les fusionner en une fonction
// « émettre » rendrait l'irréversible invisible à l'appel.

// `error_code` est conservé pour que l'appelant distingue « rien à facturer »
// (409, normal) d'une vraie panne, et affiche le message métier correspondant.
const erreurFacturation = async (r) => {
  const corps = await r.json().catch(() => ({}));
  const err = new Error(corps.error || 'Erreur');
  err.code = corps.error_code;
  err.status = r.status;
  return err;
};

export const getLicenseInvoices = async (licenseId) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/enterprise-licenses/${licenseId}/invoices`, {
    headers: getAuthHeaders(),
  });
  if (!r.ok) throw await erreurFacturation(r);
  return r.json();
};

export const createLicenseInvoice = async (licenseId, type, dateReference) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/enterprise-licenses/${licenseId}/invoices`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ type, date_reference: dateReference || undefined }),
  });
  if (!r.ok) throw await erreurFacturation(r);
  return r.json();
};

// Crée le brouillon chez QONTO et rien de plus : le PDF proforma devient
// consultable, aucun numéro légal n'est consommé, et tout reste supprimable.
export const prepareLicenseInvoice = async (invoiceId) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/invoices/${invoiceId}/prepare`, {
    method: 'POST', headers: getAuthHeaders(),
  });
  if (!r.ok) throw await erreurFacturation(r);
  return r.json();
};

export const finalizeLicenseInvoice = async (invoiceId) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/invoices/${invoiceId}/finalize`, {
    method: 'POST', headers: getAuthHeaders(),
  });
  if (!r.ok) throw await erreurFacturation(r);
  return r.json();
};

export const cancelLicenseInvoice = async (invoiceId, reason) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/invoices/${invoiceId}/cancel`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  if (!r.ok) throw await erreurFacturation(r);
  return r.json();
};

// Le PDF part en pièce jointe : on passe par un blob plutôt qu'une navigation,
// l'en-tête d'autorisation ne pouvant pas voyager dans un `window.open`.
// Un 202 n'est PAS une erreur ici : l'archivage de fond n'a pas encore abouti.
export const downloadLicenseInvoicePdf = async (invoiceId, numero) => {
  const r = await fetch(`${API_BASE_URL}/api/admin/invoices/${invoiceId}/pdf`, {
    headers: getAuthHeaders(),
  });
  if (r.status === 202) {
    const err = new Error('PDF en attente');
    err.code = 'pdf_en_attente';
    throw err;
  }
  if (!r.ok) throw await erreurFacturation(r);
  const blob = await r.blob();
  const url = window.URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = `${numero || invoiceId}.pdf`;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  window.URL.revokeObjectURL(url);
};

export { API_BASE_URL };
